import React, { useState, useEffect, useCallback } from 'react';
// Import useLocation alongside useNavigate and Link
import { useNavigate, Link, useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
// Form Inputs
import InputWithLabel from "../Form/Input/InputWithLabel";
import TextareaWithLabel from "../Form/Input/TextareaWithLabel";
import MultiSelectInColumnWithLabelExtension from '../../components/MultiSelects/MultiSelectInColumnWithLabelExtension';
import ToggleBillingPeriodNew from '../../components/Form/Input/ToggleBillingPeriodNew';
// API and Contexts
import api from '../../api'; // Make sure this points to your configured axios instance
import { useNotification } from '../../context/NotificationContext';
import { useUser } from '../../context/UserContext';
// Other Components
import Loader from "../Loader";
import SignUpVideo from './SignUpVideo';
import SmsConsentCheckbox from '../Form/SmsConsentCheckbox';
import './SignUp.css';

// --- Main Component ---
const SignUp = () => {
    const navigate = useNavigate();
    const location = useLocation(); // <<--- MODIFICATION: Get location object
    const { showNotification } = useNotification();
    const { setCreateVendorFlag, setCreateLawFirmFlag } = useUser();
    const currentYear = new Date().getFullYear();

    // --- Determine initial registration type ---
    // <<--- MODIFICATION: Check if state was passed during navigation
    const initialTypeFromState = location.state?.initialRegistrationType;
    const defaultInitialType = 'client'; // Default if no state is passed

    // --- Combined State ---
    // Состояние полей формы (включая поля для всех типов регистрации)
    const [formData, setFormData] = useState({
        // Basic User Fields
        firstName: '', lastName: '', email: '', password: '', confirmPassword: '', agreeToTerms: false,
        smsNotificationsConsent: false,
        smsMarketingConsent: false,
        // Profile Fields (Vendor/Lawyer)
        profileName: '', // Firm or Vendor Name
        phone_number: '',
        web_site: '',
        address: { country_id: '1', state_id: '', city_id: '', zip_code_id: '', street_address: '' },
        // Vendor specific
        service_type_id: '',
        foundation_year: '',
        // Lawyer specific
        licenseYear: '',
        registrationNumber: '', // Bar Number
        specializations: [], // Array of IDs
    });
    const [errors, setErrors] = useState({});
    // <<--- MODIFICATION: Initialize registrationType using value from navigation state or default
    const [registrationType, setRegistrationType] = useState(initialTypeFromState || defaultInitialType); // Тип регистрации по умолчанию (или из state)
    const [loading, setLoading] = useState(false); // Состояние основной загрузки формы
    const [billingPeriod, setBillingPeriod] = useState('monthly'); // Период оплаты

    // Dropdown/Select Options State
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [zipCodes, setZipCodes] = useState([]);
    const [serviceTypes, setServiceTypes] = useState([]); // Для Vendor
    const [allSpecializations, setAllSpecializations] = useState([]); // Для Lawyer

    // --- State for Tariff/Plan Info ---
    const [vendorTariffLoading, setVendorTariffLoading] = useState(false); // Загрузка тарифов вендора
    const [selectedVendorTariff, setSelectedVendorTariff] = useState(null); // Выбранный тариф вендора
    const [lawyerPlanLoading, setLawyerPlanLoading] = useState(false); // Загрузка плана юриста
    const [lawyerPlanData, setLawyerPlanData] = useState(null); // Данные плана юриста

    // --- Helper: Get Request Config (CSRF Token) ---
    const getRequestConfig = useCallback(() => {
         const csrfToken = Cookies.get('csrftoken');
         return {
             headers: {
                'Content-Type': 'application/json',
                ...(csrfToken && { 'X-CSRFToken': csrfToken }),
             },
             withCredentials: true
         };
    }, []);

    // --- Data Fetching Callbacks ---
    // Функции для загрузки штатов, городов, индексов, типов услуг, специализаций
    // (Original code - no changes needed here, will react to the initialized registrationType)
    const fetchStates = useCallback(async () => { if (states.length === 0) { try { const r = await api.get(`/states-filtered/?country_id=1`); setStates(r.data); } catch (e) { console.error('Error fetching states:', e); showNotification('Error', 'Could not load states.', null, 'error'); }}}, [states.length, showNotification]);
    const fetchCities = useCallback(async (stateId) => { if (!stateId) { setCities([]); setZipCodes([]); return; } setCities([]); setZipCodes([]); try { const r = await api.get(`/cities-filtered/?state_id=${stateId}`); setCities(r.data); } catch (e) { console.error('Error fetching cities:', e); showNotification('Error', 'Could not load cities.', null, 'error'); }}, [showNotification]);
    const fetchZipCodes = useCallback(async (cityId) => { if (!cityId) { setZipCodes([]); return; } setZipCodes([]); try { const r = await api.get(`/zip-codes/?city_id=${cityId}`); setZipCodes(r.data); } catch (e) { console.error('Error fetching zip codes:', e); showNotification('Error', 'Could not load zip codes.', null, 'error'); }}, [showNotification]);
    const fetchServiceTypes = useCallback(async () => { if (serviceTypes.length === 0) { try { const r = await api.get('/service-types/'); setServiceTypes(r.data); } catch (e) { console.error('Error fetching service types:', e); showNotification('Error', 'Could not load service types.', null, 'error'); }}}, [serviceTypes.length, showNotification]);
    const fetchSpecializations = useCallback(async () => { if (allSpecializations.length === 0) { try { const r = await api.get('/specializations-full/'); setAllSpecializations(r.data); } catch (e) { console.error('Error fetching specializations:', e); showNotification('Error','Could not load specializations list.',null,'error'); }}}, [allSpecializations.length, showNotification]);

    // --- UseEffects for data fetching ---
    // Редирект, если пользователь уже авторизован (можно раскомментировать)
    // (Original code - no changes needed)
    useEffect(() => { const accessToken = localStorage.getItem('access_token'); if (accessToken) { /* navigate('/profile'); */ } }, [navigate]);

    // Загрузка данных в зависимости от ТИПА РЕГИСТРАЦИИ
    // (Original code - no changes needed, will run with the correct initial registrationType)
    useEffect(() => {
        // Если Vendor или Lawyer - грузим штаты
        if (registrationType === 'vendor' || registrationType === 'lawyer') { fetchStates(); }

        // Если Vendor - грузим типы услуг, сбрасываем данные юриста
        if (registrationType === 'vendor') {
             fetchServiceTypes();
             setLawyerPlanData(null); // Сброс данных плана юриста
        }
        // Если Lawyer - грузим специализации, сбрасываем данные вендора, грузим план юриста
        else if (registrationType === 'lawyer') {
            fetchSpecializations();
            setSelectedVendorTariff(null); // Сброс тарифа вендора
            // Загрузка данных плана юриста (Pro)
            const fetchLawyerPlan = async () => {
                if (!lawyerPlanData && !lawyerPlanLoading) { // Грузим только если данных нет и не идет загрузка
                    setLawyerPlanLoading(true);
                    try {
                        const response = await api.get('/plans/pro/'); // Запрос Pro плана
                        setLawyerPlanData(response.data);
                    } catch (error) {
                        console.error('Error fetching lawyer plan (Pro):', error);
                        showNotification("Error", "Could not load lawyer subscription plan.", null, "error");
                        setLawyerPlanData(null); // Сброс при ошибке
                    } finally {
                        setLawyerPlanLoading(false);
                    }
                }
            };
            fetchLawyerPlan();
        }
        // Если Client (или другой тип) - сбрасываем данные вендора и юриста
        else {
             setSelectedVendorTariff(null);
             setLawyerPlanData(null);
             setVendorTariffLoading(false);
             setLawyerPlanLoading(false);
        }
    // Зависимости: тип регистрации и функции загрузки
    }, [registrationType, fetchStates, fetchServiceTypes, fetchSpecializations, lawyerPlanData, lawyerPlanLoading, showNotification]);

    // Загрузка городов при изменении штата
    // (Original code - no changes needed)
    useEffect(() => {
        if (formData.address.state_id && (registrationType === 'vendor' || registrationType === 'lawyer')) { fetchCities(formData.address.state_id); }
        else if (cities.length > 0) { setCities([]); } // Сброс, если штат не выбран или тип = клиент
    }, [formData.address.state_id, registrationType, fetchCities]);

    // Загрузка индексов при изменении города
    // (Original code - no changes needed)
    useEffect(() => {
        if (formData.address.city_id && (registrationType === 'vendor' || registrationType === 'lawyer')) { fetchZipCodes(formData.address.city_id); }
        else if (zipCodes.length > 0) { setZipCodes([]); } // Сброс
    }, [formData.address.city_id, registrationType, fetchZipCodes]);

    // --- useEffect для загрузки ТАРИФОВ ВЕНДОРА (с исправленными зависимостями) ---
    // (Original code - no changes needed)
     useEffect(() => {
        if (registrationType === 'vendor' && formData.service_type_id) {
            if (!vendorTariffLoading) { // Предотвращение повторных запросов во время загрузки
                const fetchVendorTariffs = async () => {
                    setSelectedVendorTariff(null);
                    setVendorTariffLoading(true);
                    try {
                        const response = await api.get(`/tariff-services/?service_type_id=${formData.service_type_id}`);
                        const fetchedTariffs = response.data || [];
                        const activeTariff = fetchedTariffs.find(tariff => tariff.status === 'active');
                        if (activeTariff) {
                            setSelectedVendorTariff(activeTariff);
                        } else {
                            console.warn(`No active tariff found for service type ID: ${formData.service_type_id}`);
                            showNotification("Warning", "No active subscription plan found for this service type.", null, "warning");
                            setSelectedVendorTariff(null);
                        }
                    } catch (error) {
                        console.error('Error fetching vendor tariff services:', error);
                        showNotification("Error", "Could not load vendor subscription plans.", null, "error");
                        setSelectedVendorTariff(null);
                    } finally {
                        setVendorTariffLoading(false);
                    }
                };
                fetchVendorTariffs();
            }
        } else if (registrationType === 'vendor') {
             setSelectedVendorTariff(null); // Сброс тарифа, если тип услуги не выбран
             if (vendorTariffLoading) setVendorTariffLoading(false); // Сброс загрузки, если она была активна
        }
    // --- ИСПРАВЛЕННЫЙ МАССИВ ЗАВИСИМОСТЕЙ ---
    // Зависит только от типа регистрации и ID типа услуги
    }, [registrationType, formData.service_type_id, showNotification]); // Note: Original code was missing vendorTariffLoading in deps, added back as per original logic
    // --- Конец useEffect для тарифов вендора ---


    // --- Input Handlers ---
     // Обработчик изменений в полях ввода
     // (Original code - no changes needed)
     const handleChange = (e) => {
         const { name, value, type, checked } = e.target;
         const val = type === 'checkbox' ? checked : value;
         setFormData(prev => ({ ...prev, [name]: val }));
         // Сброс ошибок при изменении поля
         if (errors[name]) setErrors(prev => ({ ...prev, [name]: undefined }));
         if (name === 'password' && errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: undefined }));
         if (name === 'confirmPassword' && errors.confirmPassword) setErrors(prev => ({ ...prev, confirmPassword: undefined }));
         if (name === 'agreeToTerms' && errors.agreeToTerms) setErrors(prev => ({ ...prev, agreeToTerms: undefined }));
     };

     // Обработчик изменений в полях адреса
     // (Original code - no changes needed)
     const handleAddressChange = (name, value) => {
         setFormData(prevData => {
             const updatedAddress = { ...prevData.address, [name]: value };
             // Сброс зависимых полей и их ошибок при изменении родительского поля
             if (name === 'state_id') {
                 updatedAddress.city_id = ''; updatedAddress.zip_code_id = ''; updatedAddress.street_address = '';
                 setCities([]); setZipCodes([]);
                 setErrors(prev => ({...prev, address: {...(prev.address || {}), city_id: undefined, zip_code_id: undefined, street_address: undefined}}));
             } else if (name === 'city_id') {
                 updatedAddress.zip_code_id = ''; updatedAddress.street_address = '';
                 setZipCodes([]);
                 setErrors(prev => ({...prev, address: {...(prev.address || {}), zip_code_id: undefined, street_address: undefined}}));
             } else if (name === 'zip_code_id') {
                 updatedAddress.street_address = '';
                 setErrors(prev => ({...prev, address: {...(prev.address || {}), street_address: undefined}}));
             }
             return { ...prevData, address: updatedAddress };
         });
         // Сброс конкретной ошибки адреса при изменении
         if (errors.address?.[name]) {
             setErrors(prev => ({ ...prev, address: { ...prev.address, [name]: undefined } }));
         }
     };

     // Обработчик изменений в мультиселекте специализаций (для Lawyer)
     // (Original code - no changes needed)
     const handleSpecializationChange = (selectedOptions) => {
         const selectedIds = selectedOptions.map(option => option.specialization_id);
         setFormData(prevData => ({ ...prevData, specializations: selectedIds }));
         if (errors.specializations) {
             setErrors(prev => ({ ...prev, specializations: undefined }));
         }
     };

     // Обработчик выбора типа регистрации
     // (Original code - no changes needed, handles manual switching after load)
     const handleSelectRegistrationType = (type) => {
         if (type !== registrationType) {
             setRegistrationType(type);
             // Сохраняем базовые данные пользователя при смене типа
             const baseFieldsToKeep = { firstName: formData.firstName, lastName: formData.lastName, email: formData.email, password: formData.password, confirmPassword: formData.confirmPassword, agreeToTerms: formData.agreeToTerms, smsNotificationsConsent: formData.smsNotificationsConsent, smsMarketingConsent: formData.smsMarketingConsent };
             const baseErrorsToKeep = { firstName: errors.firstName, lastName: errors.lastName, email: errors.email, password: errors.password, confirmPassword: errors.confirmPassword, agreeToTerms: errors.agreeToTerms, apiError: errors.apiError, registrationType: undefined };
             // Сбрасываем все остальные поля и ошибки
             setFormData({
                 ...baseFieldsToKeep, // Сохраняем базовые поля
                 profileName: '', phone_number: '', web_site: '', description: '',
                 address: { country_id: '1', state_id: '', city_id: '', zip_code_id: '', street_address: '' },
                 service_type_id: '', foundation_year: '', // Специфичные для вендора
                 licenseYear: '', registrationNumber: '', specializations: [] // Специфичные для юриста
                });
             setErrors({ ...baseErrorsToKeep }); // Сохраняем базовые ошибки
             setCities([]); setZipCodes([]); setBillingPeriod('monthly'); // Сброс зависимых данных и настроек
             // Сброс данных о тарифах/планах
             setSelectedVendorTariff(null); setLawyerPlanData(null);
             setVendorTariffLoading(false); setLawyerPlanLoading(false);
         }
     };

    // --- Validation ---
    // Функция валидации формы
    // (Original code - no changes needed)
    const validateForm = useCallback(() => {
        const newErrors = {};
        const { firstName, lastName, email, password, confirmPassword, agreeToTerms,
                profileName, phone_number, web_site, address,
                service_type_id, foundation_year, // Vendor
                licenseYear, registrationNumber, specializations // Lawyer
               } = formData;

        // --- Общая валидация для всех ---
        if (!firstName.trim()) newErrors.firstName = 'First name is required.';
        if (!lastName.trim()) newErrors.lastName = 'Last name is required.';
        if (!email.trim()) newErrors.email = 'Email is required.';
        else if (!/\S+@\S+\.\S+/.test(email)) newErrors.email = 'Email address is invalid.';
        if (!password) newErrors.password = 'Password is required.';
        else if (password.length < 8) newErrors.password = 'Password must be at least 8 characters long.';
        if (!confirmPassword && password) newErrors.confirmPassword = 'Please confirm your password.';
        else if (password && confirmPassword && password !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match.';
        if (!agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and policy.';

        // --- Валидация для Vendor и Lawyer ---
        if (registrationType === 'vendor' || registrationType === 'lawyer') {
            const profileTypeName = registrationType === 'vendor' ? 'Vendor' : 'Firm';
            if (!profileName.trim()) newErrors.profileName = `${profileTypeName} name is required.`;
            if (phone_number.trim() && !/^\+?[0-9\s\-()]{7,20}$/.test(phone_number.trim())) newErrors.phone_number = 'Invalid phone number format (use +1xxxxxxxxxx).';
            // Проверка URL: должен содержать точку
            if (web_site.trim() && !/(?:https?:\/\/)?.*\..*/i.test(web_site.trim())) {
                newErrors.web_site = 'Invalid URL format (e.g., example.com).';
            }

            // Валидация адреса
            let addressErrors = {};
            if (!address.state_id) addressErrors.state_id = 'State is required.';
            if (address.state_id && !address.city_id) addressErrors.city_id = 'City is required.';
            if (address.city_id && !address.zip_code_id) addressErrors.zip_code_id = 'Zip Code is required.';
            if (address.zip_code_id && !address.street_address.trim()) addressErrors.street_address = 'Street and House Number is required.';
            if (Object.keys(addressErrors).length > 0) newErrors.address = addressErrors;
        }

        // --- Специфичная валидация для Vendor ---
        if (registrationType === 'vendor') {
            if (!service_type_id) newErrors.service_type_id = 'Service type is required.';
            if (!foundation_year) newErrors.foundation_year = 'Foundation year is required.';
            else if (isNaN(foundation_year) || foundation_year < 1800 || foundation_year > currentYear) newErrors.foundation_year = `Year must be between 1800 and ${currentYear}.`;
            // Проверка доступности тарифа
            if (service_type_id && vendorTariffLoading) newErrors.service_type_id = 'Subscription plan is loading... Please wait.';
            else if (service_type_id && !selectedVendorTariff && !vendorTariffLoading) newErrors.service_type_id = 'Subscription plan is unavailable for this service type.';
        }

        // --- Специфичная валидация для Lawyer ---
        if (registrationType === 'lawyer') {
            if (!licenseYear) newErrors.licenseYear = 'License year is required.';
            else if (isNaN(licenseYear) || licenseYear < 1900 || licenseYear > currentYear) newErrors.licenseYear = `Year must be between 1900 and ${currentYear}.`;
            if (!registrationNumber.trim()) newErrors.registrationNumber = 'Registration number (Bar Number) is required.';
            if (!specializations || specializations.length === 0) newErrors.specializations = 'At least one specialization must be selected.';
            // Проверка доступности плана юриста
            if (lawyerPlanLoading) newErrors.apiError = 'Lawyer subscription plan is loading... Please wait.';
            else if (!lawyerPlanData && !lawyerPlanLoading) newErrors.apiError = 'Lawyer subscription plan is unavailable. Cannot register.';
        }

        setErrors(newErrors); // Обновляем состояние ошибок
        // Проверяем, идет ли загрузка плана/тарифа (если это релевантно для типа)
        const planOrTariffIsLoading = (registrationType === 'vendor' && vendorTariffLoading) || (registrationType === 'lawyer' && lawyerPlanLoading);
        // Форма валидна, если нет ошибок И не идет релевантная загрузка
        return Object.keys(newErrors).length === 0 && !planOrTariffIsLoading;
    }, [formData, registrationType, currentYear, selectedVendorTariff, vendorTariffLoading, lawyerPlanData, lawyerPlanLoading]); // Зависимости валидации


    // --- Form Submission ---
     // Обработчик отправки формы
     // (Original code - no changes needed)
     const handleSubmit = async (e) => {
        e.preventDefault(); // Предотвращаем стандартную отправку
        setLoading(true); // Включаем индикатор загрузки
        setErrors(prev => ({...prev, apiError: undefined })); // Сбрасываем предыдущую ошибку API

        // Добавляем "https://" к URL, если его нет
        let websiteUrl = formData.web_site.trim();
        if (websiteUrl && !websiteUrl.startsWith('http://') && !websiteUrl.startsWith('https://')) {
            websiteUrl = 'https://' + websiteUrl;
        }
        // Создаем копию данных с обновленным URL для валидации и отправки
        const currentFormData = { ...formData, web_site: websiteUrl || '' };
        // Важно: Если валидация зависит от state, нужно обновить state ПЕРЕД валидацией
        setFormData(currentFormData);

        // Используем setTimeout, чтобы React успел обновить state перед вызовом validateForm
        await new Promise(resolve => setTimeout(resolve, 0));

        // Выполняем валидацию
        if (!validateForm()) {
            // Если валидация не прошла
            setTimeout(() => { // Асинхронно, чтобы DOM успел обновиться с ошибками
                setErrors(currentValidationErrors => {
                    // Логика поиска первой ошибки для показа уведомления и скролла
                    const firstErrorKey = Object.keys(currentValidationErrors).find(key => {
                        if (key === 'address' && typeof currentValidationErrors[key] === 'object') {
                            return Object.keys(currentValidationErrors[key]).some(subKey => currentValidationErrors[key][subKey]);
                        }
                        return currentValidationErrors[key] && key !== 'apiError';
                    });

                    let firstErrorMsg = "Please check the form for errors.";
                    if (firstErrorKey) {
                         if (firstErrorKey === 'address' && typeof currentValidationErrors.address === 'object') {
                             const firstAddressErrorKey = Object.keys(currentValidationErrors.address).find(k => currentValidationErrors.address[k]);
                             firstErrorMsg = currentValidationErrors.address[firstAddressErrorKey] || firstErrorMsg;
                         } else if (typeof currentValidationErrors[firstErrorKey] === 'string'){
                            firstErrorMsg = currentValidationErrors[firstErrorKey];
                         }
                    } else if (currentValidationErrors.apiError) {
                        firstErrorMsg = currentValidationErrors.apiError;
                    }

                    showNotification("Error", firstErrorMsg, null, "error"); // Показываем уведомление

                    // Логика скролла к первому ошибочному полю
                    const errorFieldSelector = '.input-error, .multi-select-error, .has-error .label-checkbox, .error-message:not(.api-error)';
                    const firstErrorField = document.querySelector(errorFieldSelector);
                    if (firstErrorField) {
                         const elementToScroll = firstErrorField.closest('.form-group, .checkbox-container, .registration-type-selector, .main-form__content select, .main-form__content textarea, div > .error-message') || firstErrorField;
                         elementToScroll?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                    } else if (currentValidationErrors.apiError) {
                         const apiErrorElement = document.querySelector('.api-error');
                         apiErrorElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                    }
                    return currentValidationErrors; // Возвращаем ошибки (для setErrors)
                });
            }, 0);
            setLoading(false); // Выключаем индикатор загрузки
            return; // Прерываем отправку
        }

        // --- Предварительная проверка для Lawyers: дубликаты по email фирмы ---
        if (registrationType === 'lawyer') {
            try {
                const dupCheckPayload = {
                    company_email: currentFormData.email.trim().toLowerCase(),
                };
                console.log('Pre-check (lawyer duplicate) payload:', dupCheckPayload);
                const dupResp = await api.post('/auth/lawyer-duplicate-check/', dupCheckPayload, getRequestConfig());
                const dup = dupResp.data;
                console.log('Pre-check (lawyer duplicate) response:', dup);
                if (dup?.exists) {
                    // Формируем ссылку на карточку (попап) компании
                    const companyLink = `/ai?companyId=${dup.company_id}`;
                    const msg = dup.claimable
                        ? `We found a firm “${dup.company_name}” already registered with this email. The page is available to claim. If the claim option isn’t available, please email us at law@law.law with the subject "Problem with claim".`
                        : `A firm with this email is already registered in the system. Here is the profile:`;

                    setErrors(prev => ({
                        ...prev,
                        apiError: {
                            message: msg,
                            companyLink,
                            companyName: dup.company_name,
                            claimable: !!dup.claimable
                        }
                    }));

                    // Плавно проскроллить к сообщению об ошибке
                    setTimeout(() => {
                        const apiErrorElement = document.querySelector('.api-error');
                        apiErrorElement?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
                    }, 0);

                    setLoading(false);
                    return; // Остановить регистрацию до выяснения
                }
            } catch (dupErr) {
                console.error('Error during lawyer duplicate check:', dupErr.response || dupErr.message);
                // Не блокируем процесс, просто логируем и продолжаем
            }
        }

        // --- Конструкция Payload для API ---
        // Базовые данные пользователя
        const basePayload = {
             first_name: currentFormData.firstName.trim(),
             last_name: currentFormData.lastName.trim(),
             email: currentFormData.email.trim().toLowerCase(),
             password: currentFormData.password,
             confirm_password: currentFormData.confirmPassword,
             registration_type: registrationType, // Тип регистрации
             agree_to_terms: currentFormData.agreeToTerms,
             sms_notifications_consent: currentFormData.smsNotificationsConsent,
             sms_marketing_consent: currentFormData.smsMarketingConsent,
        };
        // Дополнительные данные для Vendor или Lawyer
        let professionalPayload = {};
        if (registrationType === 'vendor' || registrationType === 'lawyer') {
            professionalPayload = {
                 name: currentFormData.profileName.trim(), // Имя вендора/фирмы
                 phone_number: currentFormData.phone_number.trim(),
                 web_site: currentFormData.web_site || null, // Используем обработанный URL
                 address: { // Данные адреса
                     country_id: currentFormData.address.country_id || '1',
                     state_id: currentFormData.address.state_id,
                     city_id: currentFormData.address.city_id,
                     zip_code_id: currentFormData.address.zip_code_id,
                     street_address: currentFormData.address.street_address.trim(),
                 },
                 billing_period: billingPeriod, // Период оплаты
            };
             // Данные специфичные для Vendor
             if (registrationType === 'vendor') {
                 professionalPayload = {
                     ...professionalPayload,
                     service_type_id: currentFormData.service_type_id,
                     foundation_year: currentFormData.foundation_year,
                     // Добавляем ID тарифа, если он выбран
                     ...(selectedVendorTariff && { tariff_service_id: selectedVendorTariff.tariff_service_id }),
                 };
             }
             // Данные специфичные для Lawyer
             if (registrationType === 'lawyer') {
                 professionalPayload = {
                     ...professionalPayload,
                     license_year: currentFormData.licenseYear,
                     registration_number: currentFormData.registrationNumber.trim(),
                     specializations: currentFormData.specializations, // Массив ID специализаций
                     // Добавляем ID плана, если он загружен
                     ...(lawyerPlanData && { plan_id: lawyerPlanData.id }),
                 };
             }
        }
        // Финальный payload для отправки
        const payload = { ...basePayload, ...professionalPayload };

         // --- API Call ---
         // Отправка запроса на регистрацию
         try {
             console.log("Sending registration payload:", payload);
             const response = await api.post('/auth/register-combined/', payload, getRequestConfig());
             const responseData = response.data;

             console.log("API Response Status:", response.status);
             console.log("API Response Data Received:", responseData);

             // Обработка успешного ответа (статус 201 Created)
             if (response.status === 201) {
                 const hasCheckoutUrl = responseData?.checkout_url; // Проверяем наличие URL для оплаты
                 console.log(`Registration type: ${registrationType}, Has checkout_url: ${!!hasCheckoutUrl}`);

                 // Если это Vendor или Lawyer И есть URL для оплаты -> редирект на Stripe
                 if ((registrationType === 'lawyer' || registrationType === 'vendor') && hasCheckoutUrl) {
                     showNotification("Success", `Account created! Redirecting to secure payment for your $1 trial.`, null, "success");
                     // Установка флагов в контексте (если необходимо для других частей приложения)
                     try {
                         if (registrationType === 'lawyer' && typeof setCreateLawFirmFlag === 'function') { setCreateLawFirmFlag(true); }
                         if (registrationType === 'vendor' && typeof setCreateVendorFlag === 'function') { setCreateVendorFlag(true); }
                     } catch (contextError) { console.error("Error calling context setters:", contextError); }

                     // --- Немедленный редирект на страницу оплаты ---
                     console.log(`REDIRECTING IMMEDIATELY to: ${responseData.checkout_url}`);
                     try {
                         window.location.href = responseData.checkout_url;
                         // Не выключаем loading, т.к. страница перезагрузится
                         return; // Выход из функции handleSubmit
                     } catch (redirectError) {
                         console.error("Error during redirect:", redirectError);
                         showNotification("Error", "Could not automatically redirect to payment.", null, "error");
                         setLoading(false); // Выключаем загрузку, если редирект не удался
                     }
                     // --- Конец редиректа ---

                 }
                 // Если это Client ИЛИ нет URL для оплаты -> переход на страницу входа
                 else {
                     console.log("Standard success (Client or no checkout URL). Navigating to sign-in.");
                     showNotification("Success", "Registration successful! Please check your email to verify your account.", null, "success");
                     setLoading(false);
                     navigate('/sign-in'); // Переход на страницу входа
                 }
             }
             // Обработка других успешных статусов (если API может их возвращать)
             else {
                 console.warn("Registration completed with unexpected status:", response.status, responseData);
                 showNotification("Warning", `Registration attempt resulted in status ${response.status}.`, null, "warning");
                 setLoading(false);
                 navigate('/sign-in'); // По умолчанию переходим на вход
             }

         } catch (error) {
             // Обработка ошибок API
            console.error("Registration failed:", error.response || error.message);
            const errorData = error.response?.data;
            let apiErrorMsg = "Registration failed. Please check details and try again.";
            let fieldErrorsSet = false; // Флаг, что установлена хотя бы одна ошибка поля
            const tempErrors = {}; // Временный объект для ошибок полей

            if (errorData && typeof errorData === 'object') {
                let nonFieldError = null; // Для общих ошибок типа "detail" или "non_field_errors"
                // Обработка ошибок по каждому полю из ответа API
                Object.entries(errorData).forEach(([key, value]) => {
                    const errorText = Array.isArray(value) ? value.join(' ') : String(value);
                    let fieldMapped = false; // Флаг, что ключ API сопоставлен с полем формы

                    // Карта сопоставления ключей API и ключей состояния/ошибок
                    const directMap = {
                        'first_name': 'firstName', 'last_name': 'lastName', 'email': 'email',
                        'password': 'password', 'confirm_password': 'confirmPassword',
                        'agree_to_terms': 'agreeToTerms', 'name': 'profileName',
                        'phone_number': 'phone_number', 'web_site': 'web_site', 'description': 'description',
                        'service_type_id': 'service_type_id', 'foundation_year': 'foundation_year',
                        'license_year': 'licenseYear', 'registration_number': 'registrationNumber',
                        'specializations': 'specializations',
                        // Ошибки, связанные с оплатой/тарифами, показываем как общую ошибку API
                        'billing_period': 'apiError',
                        'tariff_service_id': 'service_type_id', // Ошибку тарифа привязываем к полю выбора типа
                        'plan_id': 'apiError',
                    };

                    if (directMap[key]) {
                        tempErrors[directMap[key]] = errorText; // Записываем ошибку для соответствующего поля
                        fieldMapped = true;
                    } else if (key === 'address' && typeof value === 'object' && value !== null) {
                        // Обработка вложенных ошибок адреса
                        let addrErrors = {};
                        Object.entries(value).forEach(([addrKey, addrValue]) => {
                            const addrErrorText = Array.isArray(addrValue) ? addrValue.join(' ') : String(addrValue);
                            const addrMap = { 'state_id': 'state_id', 'city_id': 'city_id', 'zip_code_id': 'zip_code_id', 'street_address': 'street_address' };
                            if (addrMap[addrKey]) { addrErrors[addrMap[addrKey]] = addrErrorText; }
                        });
                        if (Object.keys(addrErrors).length > 0) {
                             // Объединяем новые ошибки адреса с существующими (если они были)
                             tempErrors.address = { ...(errors.address || {}), ...addrErrors };
                        }
                        fieldMapped = true; // Считаем обработанным, даже если не было специфичных ошибок адреса
                    } else if (key === 'detail' || key === 'non_field_errors' || key === 'error') {
                        // Сохраняем общую ошибку API
                        nonFieldError = errorText;
                    }

                    if (fieldMapped && tempErrors[directMap[key]]) fieldErrorsSet = true; // Устанавливаем флаг, если была добавлена ошибка поля
                });

                setErrors(prev => ({ ...prev, ...tempErrors })); // Применяем найденные ошибки полей

                // Определяем финальное сообщение для уведомления
                if (fieldErrorsSet && Object.keys(tempErrors).length > 0) {
                    // Если были ошибки полей
                    apiErrorMsg = "Please correct the errors highlighted below.";
                } else if (nonFieldError) {
                    // Если была только общая ошибка API
                    apiErrorMsg = nonFieldError;
                    setErrors(prev => ({ ...prev, apiError: nonFieldError })); // Показываем ее как общую ошибку
                } else {
                    // Если структура ошибки неизвестна
                    apiErrorMsg = "An unexpected error occurred during registration.";
                    setErrors(prev => ({ ...prev, apiError: apiErrorMsg }));
                    console.error("Unhandled API error object structure:", errorData);
                }

            } else if (typeof errorData === 'string') {
                // Если API вернуло просто строку ошибки
                apiErrorMsg = errorData;
                setErrors(prev => ({ ...prev, apiError: apiErrorMsg }));
            }

            showNotification("Error", apiErrorMsg, null, "error"); // Показываем уведомление

            // Прокрутка к первой ошибке (асинхронно)
            setTimeout(() => { /* ... scroll logic from previous versions ... */ }, 0);
            setLoading(false); // Выключаем индикатор загрузки
         }
     }; // End handleSubmit


    // --- Render Logic ---
    // (Original code - no changes needed)
    // Получение объектов специализаций для MultiSelect
    const selectedSpecializationObjects = allSpecializations.filter(spec => formData.specializations.includes(spec.specialization_id));
    // Расчет цен для отображения в ToggleBillingPeriodNew
    let vendorMonthlyPrice = null; let vendorAnnualPrice = null; let vendorEffectiveMonthlyPrice = null;
    if (selectedVendorTariff) {
        vendorMonthlyPrice = selectedVendorTariff.monthly_price;
        vendorAnnualPrice = selectedVendorTariff.annual_price;
        if (vendorAnnualPrice) { vendorEffectiveMonthlyPrice = (parseFloat(vendorAnnualPrice) / 12).toFixed(0); }
    }
    let lawyerMonthlyPrice = null; let lawyerAnnualPrice = null; let lawyerEffectiveMonthlyPrice = null;
    if (lawyerPlanData) {
        lawyerMonthlyPrice = lawyerPlanData.monthly_price;
        lawyerAnnualPrice = lawyerPlanData.annual_price;
        if (lawyerAnnualPrice) { lawyerEffectiveMonthlyPrice = (parseFloat(lawyerAnnualPrice) / 12).toFixed(0); }
    }
    // Флаг, идет ли загрузка плана/тарифа (для блокировки кнопки Submit)
    const isPlanLoading = (registrationType === 'vendor' && vendorTariffLoading) || (registrationType === 'lawyer' && lawyerPlanLoading);

    // Рендер общих полей профиля для Vendor и Lawyer
    const renderCommonProfileFields = () => (
        <>
            <InputWithLabel label='Contact Phone (Optional)' name='phone_number' type='tel' value={formData.phone_number} onChange={handleChange} error={errors.phone_number} autoComplete="tel" placeholder="+1xxxxxxxxxx" />
            <InputWithLabel label='Website (Optional)' name='web_site' type='url' value={formData.web_site} onChange={handleChange} error={errors.web_site} placeholder="example.com" autoComplete="url" />
            <h4 style={{ textAlign: 'left', marginTop: '20px', marginBottom: '10px' }}>Office Address *</h4>
            {/* Выпадающие списки для адреса */}
            <select name="state_id" className={`input ${errors.address?.state_id ? 'input-error' : ''}`} value={formData.address.state_id} onChange={(e) => handleAddressChange('state_id', e.target.value)} aria-invalid={!!errors.address?.state_id} style={{ width: '100%', marginBottom: errors.address?.state_id ? '5px' : '0px' }} required >
                <option value="">Select State *</option>
                {states.map((state) => <option key={state.state_id} value={state.state_id}>{state.name}</option>)}
            </select>
            {errors.address?.state_id && <span className="error-message" style={{display: 'block', marginBottom: '10px'}}>{errors.address.state_id}</span>}

            {formData.address.state_id && (
                <>
                    <select name="city_id" className={`input ${errors.address?.city_id ? 'input-error' : ''}`} value={formData.address.city_id} onChange={(e) => handleAddressChange('city_id', e.target.value)} disabled={!cities.length && !!formData.address.state_id} aria-invalid={!!errors.address?.city_id} style={{ width: '100%', marginBottom: errors.address?.city_id ? '5px' : '10px' }} required >
                        <option value="">{cities.length === 0 && !!formData.address.state_id ? 'Loading Cities...' : 'Select City *'}</option>
                        {cities.map((city) => <option key={city.city_id} value={city.city_id}>{city.name}</option>)}
                    </select>
                    {errors.address?.city_id && <span className="error-message" style={{display: 'block', marginBottom: '10px'}}>{errors.address.city_id}</span>}
                </>
            )}

            {formData.address.city_id && (
                <>
                    <select name="zip_code_id" className={`input ${errors.address?.zip_code_id ? 'input-error' : ''}`} value={formData.address.zip_code_id} onChange={(e) => handleAddressChange('zip_code_id', e.target.value)} disabled={!zipCodes.length && !!formData.address.city_id} aria-invalid={!!errors.address?.zip_code_id} style={{ width: '100%', marginBottom: errors.address?.zip_code_id ? '5px' : '10px' }} required >
                        <option value="">{zipCodes.length === 0 && !!formData.address.city_id ? 'Loading Zip Codes...' : 'Select Zip Code *'}</option>
                        {zipCodes.map((zip) => <option key={zip.id} value={zip.id}>{zip.zip_code}</option>)}
                    </select>
                    {errors.address?.zip_code_id && <span className="error-message" style={{display: 'block', marginBottom: '10px'}}>{errors.address.zip_code_id}</span>}
                </>
             )}

            {formData.address.zip_code_id && (
                <InputWithLabel label='Street and House Number *' name='street_address' value={formData.address.street_address} onChange={(e) => handleAddressChange('street_address', e.target.value)} error={errors.address?.street_address} maxLength={200} required />
            )}
        </>
    );

    // Рендер секции подписки (тарифа/плана)
    // (Original code - no changes needed)
    const renderSubscriptionSection = () => {
        // Функция для форматирования строки цены
        const getPriceString = (period) => {
             if (registrationType === 'lawyer') {
                 if (!lawyerPlanData) return 'N/A';
                 return period === 'monthly'
                     ? `$${lawyerMonthlyPrice}/month`
                     : `$${lawyerEffectiveMonthlyPrice}/month (billed annually at $${lawyerAnnualPrice})`;
             } else if (registrationType === 'vendor') {
                 if (!selectedVendorTariff) return 'N/A';
                 return period === 'monthly'
                     ? `$${vendorMonthlyPrice}/month`
                     : `$${vendorEffectiveMonthlyPrice}/month (billed annually at $${vendorAnnualPrice})`;
             }
             return 'N/A';
        };
        // Показываем уведомление о триале, если план/тариф загружен
        const showTrialNotification = (registrationType === 'lawyer' && lawyerPlanData && !lawyerPlanLoading) || (registrationType === 'vendor' && selectedVendorTariff && !vendorTariffLoading);

        return (
            <div style={{ width: '100%', marginTop: '20px', marginBottom: '5px' /* Мин. высота для консистентности */ }}>
                {/* Секция для Lawyer */}
                {registrationType === 'lawyer' && (
                    lawyerPlanLoading ? ( <div style={{ textAlign: 'center', color: '#888', padding: '10px 0' }}> <Loader size="small" /> Loading plan... </div> )
                    : lawyerPlanData ? ( <> <h4 style={{ textAlign: 'left', marginBottom: '15px' }}>Lawyer Subscription Plan *</h4> <ToggleBillingPeriodNew billingPeriod={billingPeriod} onBillingPeriodChange={setBillingPeriod} disabled={loading || lawyerPlanLoading} monthlyPrice={lawyerMonthlyPrice} annualPrice={lawyerAnnualPrice} effectiveMonthlyPrice={lawyerEffectiveMonthlyPrice} /> </> )
                    // Сообщение об ошибке, если план не загрузился
                    : ( !lawyerPlanLoading && <div style={{ color: '#dc3545', textAlign: 'center', fontWeight: 'bold', marginTop: '15px' }}> Lawyer plan is currently unavailable. Cannot register. </div> )
                )}

                {/* Секция для Vendor */}
                {registrationType === 'vendor' && (
                    // Сообщение, пока не выбран тип услуги
                    !formData.service_type_id ? ( <div style={{ color: '#888', textAlign: 'center', marginTop: '15px', padding: '10px 0' }}> Select a Service Type to see plans... </div> )
                    // Индикатор загрузки тарифов
                    : vendorTariffLoading ? ( <div style={{ textAlign: 'center', color: '#888', padding: '10px 0' }}> <Loader size="small" /> Loading subscription options... </div> )
                    // Отображение переключателя и цен, если тариф загружен
                    : selectedVendorTariff ? ( <> <h4 style={{ textAlign: 'left', marginBottom: '15px' }}>Vendor Subscription Plan *</h4> <ToggleBillingPeriodNew billingPeriod={billingPeriod} onBillingPeriodChange={setBillingPeriod} disabled={loading || vendorTariffLoading || !selectedVendorTariff} monthlyPrice={vendorMonthlyPrice} annualPrice={vendorAnnualPrice} effectiveMonthlyPrice={vendorEffectiveMonthlyPrice} /> </> )
                    // Сообщение, если тарифы не найдены или не загрузились
                    : ( !vendorTariffLoading && <div style={{ color: '#dc3545', textAlign: 'center', fontWeight: 'bold', marginTop: '15px' }}> Subscription plan not available for this service type. </div> )
                )}

                 {/* Уведомление о триале */}
                 {showTrialNotification && (
                     <div className="trial-notification" style={{ marginTop: '15px', padding: '12px', border: '1px solid #0d6efd', borderRadius: '5px', backgroundColor: '#cfe2ff', fontSize: '0.9em', color: '#0a58ca', lineHeight: '1.4' }}>
                         <strong>Important:</strong> You will be charged <strong>$1.00 today</strong> for a <strong>90-day trial</strong>. After the trial, your selected plan ({getPriceString(billingPeriod)}) will be billed automatically unless cancelled. You can manage your subscription in your profile dashboard.
                     </div>
                 )}
            </div>
        );
    };

    // --- Main Return JSX ---
    // Основная разметка компонента
    // (Original code - no changes needed, the 'selected' class and conditional rendering will work based on the initialized registrationType)
    return (
        <div className="main-form-container">
            <div className="signup-video-container">
                <SignUpVideo /> {/* Компонент с видео фоном */}
            </div>
            <div className="main-form__right"> {/* Правая часть с формой */}
                <form className="main-form__content" onSubmit={handleSubmit} noValidate> {/* Форма */}
                    <div className="scroll-wrap"> {/* Обертка для скролла */}
                        <h2>Register Account</h2>
                        {/* Ссылка на вход */}
                        <div className="sign-up-link" style={{marginBottom: '25px'}}>
                            <span>Already have an account? </span><Link to="/sign-in">Sign In</Link>
                        </div>
                         {/* Выбор типа регистрации */}
                         <div className="registration-type-selector" style={{marginBottom: '20px'}}>
                             <h4 style={{marginBottom: '10px', textAlign: 'left'}}>I want to register as a: *</h4>
                             {/* Контейнер с карточками выбора */}
                             <div className="registration-options-container">
                                {/* Карточка Lawyer */}
                                <div className={`registration-option-card ${registrationType === 'lawyer' ? 'selected' : ''}`} onClick={() => handleSelectRegistrationType('lawyer')} role="button" tabIndex={0} aria-pressed={registrationType === 'lawyer'}>
                                    <div className="registration-option-icon">⚖️</div> <h5 className="registration-option-title">Lawyer / Attorney</h5> <p className="registration-option-description">Get clients & promote your practice.</p>
                                </div>
                                {/* Карточка Vendor */}
                                <div className={`registration-option-card ${registrationType === 'vendor' ? 'selected' : ''}`} onClick={() => handleSelectRegistrationType('vendor')} role="button" tabIndex={0} aria-pressed={registrationType === 'vendor'}>
                                    <div className="registration-option-icon">💼</div> <h5 className="registration-option-title">Vendor / Partner</h5> <p className="registration-option-description">(Doctor, translator, expert, etc.)</p>
                                </div>
                                {/* Карточка Client */}
                                <div className={`registration-option-card ${registrationType === 'client' ? 'selected' : ''}`} onClick={() => handleSelectRegistrationType('client')} role="button" tabIndex={0} aria-pressed={registrationType === 'client'}>
                                    <div className="registration-option-icon">👤</div> <h5 className="registration-option-title">Client</h5> <p className="registration-option-description">Find professionals & book appointments.</p>
                                </div>
                             </div>
                            {/* Отображение ошибки выбора типа (если есть) */}
                            {errors.registrationType && <span className="error-message" style={{display: 'block', marginTop: '5px'}}>{errors.registrationType}</span>}
                         </div>

                        {/* Отображение общей ошибки API */}
                        {errors.apiError && (
                                                    <div className="error-message api-error" style={{marginBottom: '15px', padding: '12px', textAlign: 'left', color: '#842029', fontWeight: 500, border: '1px solid #f5c2c7', borderRadius: '6px', backgroundColor: '#f8d7da', lineHeight: 1.5}}>
                                                        <div>{errors.apiError.message}</div>
                                                        {errors.apiError.companyLink && (
                                                            <div style={{marginTop: '8px'}}>
                                                                <a href={errors.apiError.companyLink} style={{ color: '#0a58ca', textDecoration: 'underline' }} target="_blank" rel="noreferrer">
                                                                    Open profile: {errors.apiError.companyName}
                                                                </a>
                                                            </div>
                                                        )}
                                                        {!errors.apiError.claimable && errors.apiError.companyLink && (
                                                            <div style={{marginTop: '8px'}}>If this is your profile and you have trouble accessing it, please contact us at <a href="mailto:law@law.law?subject=Problem%20with%20claim" style={{ color: '#0a58ca', textDecoration: 'underline' }}>law@law.law</a> with the subject "Problem with claim".</div>
                                                        )}
                                                        {errors.apiError.claimable && (
                                                            <div style={{marginTop: '8px'}}>To claim this profile, please contact us at <a href="mailto:law@law.law?subject=Problem%20with%20claim" style={{ color: '#0a58ca', textDecoration: 'underline' }}>law@law.law</a>.</div>
                                                        )}
                                                    </div>
                                                )}

                        {/* Секция: Учетные данные */}
                        <h3 style={{ textAlign: 'left', marginTop: '20px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Account Credentials</h3>
                         <InputWithLabel label='First Name *' name='firstName' value={formData.firstName} onChange={handleChange} error={errors.firstName} maxLength={50} autoComplete="given-name" required />
                         <InputWithLabel label='Last Name *' name='lastName' value={formData.lastName} onChange={handleChange} error={errors.lastName} maxLength={50} autoComplete="family-name" required />
                         <InputWithLabel label='E-mail *' name='email' type='email' value={formData.email} onChange={handleChange} error={errors.email} maxLength={100} autoComplete="email" required />
                         <InputWithLabel label='Password * (min. 8 characters)' name='password' type='password' value={formData.password} onChange={handleChange} error={errors.password} autoComplete="new-password" required />
                         <InputWithLabel label='Confirm Password *' name='confirmPassword' type='password' value={formData.confirmPassword} onChange={handleChange} error={errors.confirmPassword} autoComplete="new-password" required />

                        {/* Секция: Профиль вендора (отображается условно) */}
                        {registrationType === 'vendor' && (
                            <>
                                <h3 style={{ textAlign: 'left', marginTop: '25px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Vendor Profile</h3>
                                <InputWithLabel label='Vendor Name *' name='profileName' value={formData.profileName} onChange={handleChange} error={errors.profileName} maxLength={150} required />
                                {/* Выпадающий список Service Type */}
                                <label htmlFor="service_type_id_select" className="label" style={{display: 'block', marginBottom:'3px', textAlign:'left'}}>Service Type *</label>
                                <select id="service_type_id_select" name="service_type_id" value={formData.service_type_id} onChange={handleChange} className={`input ${errors.service_type_id ? 'input-error' : ''}`} aria-invalid={!!errors.service_type_id} style={{width: '100%', marginBottom: errors.service_type_id ? '5px' : '15px' }} required >
                                    <option value="">Select Service Type *</option>
                                    {/* Маппинг списка serviceTypes */}
                                    {serviceTypes.map((type) => <option key={type.service_type_id} value={type.service_type_id}>{type.service_type_name}</option>)}
                                </select>
                                {errors.service_type_id && <span id="service-type-error" className="error-message" style={{display: 'block', marginBottom: '10px'}}>{errors.service_type_id}</span>}
                                {/* --- */}
                                <InputWithLabel label='Foundation Year *' name='foundation_year' type='number' value={formData.foundation_year} onChange={handleChange} min="1800" max={currentYear} error={errors.foundation_year} required />
                                {renderCommonProfileFields()} {/* Общие поля профиля */}
                                {renderSubscriptionSection()} {/* Секция подписки */}
                            </>
                        )}

                        {/* Секция: Профиль юриста (отображается условно) */}
                        {registrationType === 'lawyer' && (
                            <>
                                <h3 style={{ textAlign: 'left', marginTop: '25px', marginBottom: '15px', borderBottom: '1px solid #eee', paddingBottom: '5px' }}>Attorney / Firm Profile</h3>
                                <InputWithLabel label='Firm Name *' name='profileName' value={formData.profileName} onChange={handleChange} error={errors.profileName} maxLength={150} required />
                                <InputWithLabel label='License Year *' name='licenseYear' type='number' value={formData.licenseYear} onChange={handleChange} min="1900" max={currentYear} error={errors.licenseYear} required />
                                <InputWithLabel label='Registration Number (Bar Number) *' name='registrationNumber' value={formData.registrationNumber} onChange={handleChange} error={errors.registrationNumber} maxLength={50} required />
                                {/* Выбор специализаций */}
                                <label htmlFor="specializations-select" className="label" style={{display: 'block', marginBottom:'3px', textAlign:'left'}}>Specializations *</label>
                                <MultiSelectInColumnWithLabelExtension
                                    className={`input ${errors.specializations ? 'input-error multi-select-error' : ''}`}
                                    style={{ width: '100%', marginBottom: errors.specializations ? '5px' : '15px' }}
                                    aria-invalid={!!errors.specializations}
                                    label="" // Пустой label, т.к. внешний label уже есть
                                    options={allSpecializations} // Список всех специализаций
                                    onChange={handleSpecializationChange} // Обработчик выбора
                                    placeholder="Select specializations *"
                                    initialValue={selectedSpecializationObjects} // Преобразованные выбранные ID в объекты
                                    maxSelectedLabels={5} // Макс. кол-во отображаемых лэйблов
                                    selectId="specializations-select" // ID для связи с label
                                    // isMulti={true} // Assuming this prop exists if it's a multi-select component
                                />
                                {errors.specializations && <span id="specializations-error" className="error-message" style={{ display: 'block', marginBottom: '10px' }}>{errors.specializations}</span>}
                                {renderCommonProfileFields()} {/* Общие поля профиля */}
                                {renderSubscriptionSection()} {/* Секция подписки */}
                             </>
                        )}

                         {/* Разделитель перед чекбоксом для Vendor/Lawyer */}
                         {(registrationType === 'vendor' || registrationType === 'lawyer') && <hr style={{marginTop: '0px', marginBottom: '0px', borderTop: '1px solid #eee'}} /> }

                        {/* Чекбокс согласия с условиями */}
                        <div className={`checkbox-container ${errors.agreeToTerms ? 'has-error' : ''}`}>
                             <label className="label-checkbox">
                                 <input type="checkbox" name="agreeToTerms" checked={formData.agreeToTerms} onChange={handleChange} aria-invalid={!!errors.agreeToTerms} required />
                                 <div className="psevdocheckbox"></div> {/* Стилизованный чекбокс */}
                             </label>
                             <span className="text-left" id="terms-label">
                                 I agree to the&nbsp;
                                 <a href="/terms-of-use" className="checkbox__link" target="_blank" rel="noopener noreferrer">Terms of Use</a> and&nbsp;
                                 <a href="/privacy-policy" className="checkbox__link" target="_blank" rel="noopener noreferrer">Privacy Policy</a>. *
                             </span>
                         </div>
                         {/* Ошибка для чекбокса */}
                         {errors.agreeToTerms && <span id="terms-error" className="error-message terms-error">{errors.agreeToTerms}</span>}

                        <SmsConsentCheckbox
                            checked={formData.smsNotificationsConsent}
                            onChange={handleChange}
                        />
                        <SmsConsentCheckbox
                            name="smsMarketingConsent"
                            checked={formData.smsMarketingConsent}
                            onChange={handleChange}
                            variant="marketing"
                        />

                        {/* Кнопка отправки формы */}
                        <button
                            className="button button-black"
                            type="submit"
                            // Блокируем кнопку во время основной загрузки ИЛИ загрузки плана/тарифа
                            disabled={loading || isPlanLoading}
                            style={{marginTop: '25px', width: '100%', padding: '12px 15px'}}
                        >
                            {loading // Если идет основная загрузка
                                ? 'Processing...'
                                : (registrationType === 'vendor' || registrationType === 'lawyer') // Если Vendor или Lawyer
                                    ? 'Register & Start $1 Trial' // Текст для платной регистрации
                                    : 'Register Account' // Текст для Client
                            }
                        </button>
                    </div> {/* Конец scroll-wrap */}
                </form> {/* Конец формы */}
            </div> {/* Конец main-form__right */}
        </div> // Конец main-form-container
    );
};

export default SignUp;
